/**
 * Keyword definitions from the Legendary Wars rulebook
 * These provide tooltip explanations for game mechanics
 */

export interface KeywordDefinition {
  name: string;
  description: string;
  category?: 'combat' | 'status' | 'type' | 'ability' | 'damage';
}

export const keywordDefinitions: Record<string, KeywordDefinition> = {
  // Character Types
  'Warrior': {
    name: 'Warrior',
    description: 'May attack once from up to two sources per phase.',
    category: 'type'
  },
  'Wizard': {
    name: 'Wizard',
    description: 'May use a third phase to either ready or cast a spell.',
    category: 'type'
  },
  'Fighter': {
    name: 'Fighter',
    description: 'May attack twice during one of their two phases.',
    category: 'type'
  },
  'Berserker': {
    name: 'Berserker',
    description: 'If a berserker loses an attack within 2 paces, the attack deals instant ½ damage. Berserkers can only be knocked down by berserkers.',
    category: 'type'
  },
  'Elite': {
    name: 'Elite',
    description: 'May reroll one roll each turn and when defending.',
    category: 'type'
  },
  'Head of the Order': {
    name: 'Head of the Order',
    description: 'Each team may only have one HotO OR Supernatural player on it, not both.',
    category: 'type'
  },
  'HotO': {
    name: 'Head of the Order',
    description: 'Each team may only have one HotO OR Supernatural player on it, not both.',
    category: 'type'
  },
  'Supernatural': {
    name: 'Supernatural',
    description: 'Powerful beings with unique abilities. Each team may only have one HotO OR Supernatural player.',
    category: 'type'
  },
  'Dark': {
    name: 'Dark',
    description: 'Dark and Light players cannot be allied.',
    category: 'type'
  },
  'Light': {
    name: 'Light',
    description: 'Light and Dark players cannot be allied.',
    category: 'type'
  },
  'Legendary': {
    name: 'Legendary',
    description: 'Exceptionally powerful characters with unique legendary abilities.',
    category: 'type'
  },
  'Maiden': {
    name: 'Maiden',
    description: 'May make one of their attacks instant during the game except against other maidens.',
    category: 'type'
  },
  'Commander': {
    name: 'Commander',
    description: 'Begin the game with a specific Command Post with set health and abilities. May spend a phase to summon listed minions or companions from the Command Post.',
    category: 'type'
  },
  'Conjurer': {
    name: 'Conjurer',
    description: 'Minions and companions this player creates remain in play after their death and must be killed to win the game.',
    category: 'type'
  },
  'Bender': {
    name: 'Bender',
    description: 'These players\' abilities do not count as magical damage.',
    category: 'type'
  },
  'Angelic': {
    name: 'Angelic',
    description: 'Divine beings with holy powers and resistances.',
    category: 'type'
  },
  'Archaic': {
    name: 'Archaic',
    description: 'Ancient beings with timeless powers.',
    category: 'type'
  },
  'Demon': {
    name: 'Demon',
    description: 'Infernal beings with dark powers.',
    category: 'type'
  },
  'Demonic': {
    name: 'Demonic',
    description: 'Related to or possessed by demons.',
    category: 'type'
  },

  // Combat Keywords
  'Armored': {
    name: 'Armored',
    description: 'Decrease the damage of non-magical weapons by X (where X is the armor value).',
    category: 'combat'
  },
  'Battle IQ': {
    name: 'Battle IQ',
    description: 'This character receives no defense reduction from fighting multiple opponents.',
    category: 'combat'
  },
  'Piercing': {
    name: 'Piercing',
    description: 'May deal up to 3 damage that armor reduces.',
    category: 'damage'
  },
  'Bonebreaker': {
    name: 'Bonebreaker',
    description: 'If this weapon deals full damage to a player who is not armored or supernatural, may choose to reduce either their attack and damage or defense and speed by ½ until that player is healed.',
    category: 'damage'
  },
  'Shield Breaker': {
    name: 'Shield Breaker',
    description: 'If an enemy successfully defends against this weapon\'s attack, destroy their shield if they have one.',
    category: 'combat'
  },
  'Shieldbreaker': {
    name: 'Shield Breaker',
    description: 'If an enemy successfully defends against this weapon\'s attack, destroy their shield if they have one.',
    category: 'combat'
  },
  'Overkill': {
    name: 'Overkill',
    description: 'Deal x2 damage to minions and companions.',
    category: 'damage'
  },
  'Disarm': {
    name: 'Disarm',
    description: 'If a weapon is disarmed, the weapon is dropped 1 pace in any direction of the disarmer\'s choosing.',
    category: 'combat'
  },

  // Status Effects
  'Knocked down': {
    name: 'Knocked Down',
    description: 'A player must use a phase to get up after being knocked down at the beginning of their next turn and their defense is decreased to 0 while in this position.',
    category: 'status'
  },
  'Binding': {
    name: 'Binding',
    description: 'If a player is bound by an ability, they cannot attack, move, or defend against non-Will attacks, unless they have a strength attack of 8 or greater.',
    category: 'status'
  },
  'Cursed': {
    name: 'Cursed',
    description: 'Deal 3 magical damage to the victim each of their turns and decrease their defense by 3 unless they are healed by magic, supernatural abilities, or touched by a water spell (but not from Force abilities).',
    category: 'status'
  },
  'Poisoned': {
    name: 'Poisoned',
    description: 'The victim takes 5 non-magical damage every turn and their defense is decreased by 2 unless they are healed by any means.',
    category: 'status'
  },
  'Set on fire': {
    name: 'Set on Fire',
    description: 'If a player is set on fire they take 2 non-magic damage and must immediately move ½ their maximum paces and get into a knocked down position.',
    category: 'status'
  },
  'Flying': {
    name: 'Flying',
    description: 'Players take 0 falling damage unless knocked down, and may pass over objects and players. May move up levels as 1 pace, and is always treated as being one level above ground unless by consent or being bound.',
    category: 'ability'
  },

  // Weapon/Item Properties
  'Blessed': {
    name: 'Blessed',
    description: 'Cannot be used by a Dark player, deals 2 magical damage to any Dark players and an extra 5 magical damage to dark supernatural or undead enemies, and cannot be cursed.',
    category: 'ability'
  },
  'Elven': {
    name: 'Elven',
    description: 'Deals 4 magical damage to dark enemies and cannot be destroyed except by a legendary weapon.',
    category: 'ability'
  },
  'Enchanted': {
    name: 'Enchanted',
    description: 'Counts as magical damage in addition to physical damage.',
    category: 'damage'
  },
  'Crystal Weapons': {
    name: 'Crystal Weapons',
    description: 'Deal total damage irrespective of armor and have Shield Breaker.',
    category: 'ability'
  },

  // Damage Types
  'physical': {
    name: 'Physical',
    description: 'Standard physical damage from weapons and strength attacks. Can be reduced by armor.',
    category: 'damage'
  },
  'magical': {
    name: 'Magical',
    description: 'Magical damage that bypasses armor but may be resisted by supernatural beings.',
    category: 'damage'
  },
  'fire': {
    name: 'Fire',
    description: 'Fire damage that can set targets ablaze, causing ongoing damage and forced movement.',
    category: 'damage'
  },
  'lightning': {
    name: 'Lightning',
    description: 'Electrical damage that strikes with devastating speed.',
    category: 'damage'
  },
  'ice': {
    name: 'Ice',
    description: 'Freezing damage that can slow or immobilize targets.',
    category: 'damage'
  },
  'water': {
    name: 'Water',
    description: 'Water-based damage that can extinguish fires and cleanse curses.',
    category: 'damage'
  },
  'poison': {
    name: 'Poison',
    description: 'Toxic damage that causes ongoing harm and weakens defenses.',
    category: 'damage'
  },
  'will': {
    name: 'Will',
    description: 'Mental/psychic attacks that target the mind rather than the body. Uses Will stat for attack and defense.',
    category: 'damage'
  },

  // Special Mechanics
  'Immunity to': {
    name: 'Immunity',
    description: 'Character is immune to the effects and damage of the specified type.',
    category: 'ability'
  },
  'Companion': {
    name: 'Companion',
    description: 'Companions are allowed to roll a defensive roll, whereas minions are dealt instant damage.',
    category: 'type'
  },
  'Minion': {
    name: 'Minion',
    description: 'Minions are dealt instant damage without a defensive roll, unlike companions.',
    category: 'type'
  },
  'Demon Spirits': {
    name: 'Demon Spirits',
    description: 'When summoned, they can immediately enter a corpse or minion. While in their vessel, they cannot be damaged by non-magic attacks, and have a speed of 8 and a strength attack damage of 5 within 2 paces.',
    category: 'ability'
  },
  'Destruction': {
    name: 'Destruction',
    description: 'If a player\'s corpse is set on fire or dealt an attack with over 8 damage, remove their body from the field.',
    category: 'ability'
  },
  'Fall': {
    name: 'Fall',
    description: 'If a player is pushed off of a higher level, take ½ the number of paces fallen as damage and place them in a Knocked Down position.',
    category: 'ability'
  },
  'Push damage': {
    name: 'Push Damage',
    description: 'If a player is pushed into a wall, then ½ the remaining amount of spaces they needed to move is deducted from their health. If pushed into another player, both are knocked down.',
    category: 'damage'
  },
  'Potions': {
    name: 'Potions',
    description: 'Potions used within 2 paces of a target are instant.',
    category: 'ability'
  },
  'Legendary Weapons': {
    name: 'Legendary Weapons',
    description: 'Excalibur, Sword of Truth, Sword of the Griffin, The Sword of Righteousness, the Archangel and Enemy\'s blade, The Rings of Power, and Death\'s Wand.',
    category: 'ability'
  }
};

/**
 * Get keyword definition by name (case-insensitive)
 */
export function getKeywordDefinition(keyword: string): KeywordDefinition | undefined {
  const normalizedKeyword = keyword.trim();
  
  // Direct match
  if (keywordDefinitions[normalizedKeyword]) {
    return keywordDefinitions[normalizedKeyword];
  }
  
  // Case-insensitive match
  const lowerKeyword = normalizedKeyword.toLowerCase();
  const matchingKey = Object.keys(keywordDefinitions).find(
    key => key.toLowerCase() === lowerKeyword
  );
  
  if (matchingKey) {
    return keywordDefinitions[matchingKey];
  }
  
  // Check if it starts with "Armored" (e.g., "Armored 4")
  if (lowerKeyword.startsWith('armored')) {
    return keywordDefinitions['Armored'];
  }
  
  // Check if it contains "Immunity to" (e.g., "Immunity to fire")
  if (lowerKeyword.includes('immunity') || lowerKeyword.includes('immune')) {
    return keywordDefinitions['Immunity to'];
  }
  
  return undefined;
}

/**
 * Check if a keyword has a definition
 */
export function hasKeywordDefinition(keyword: string): boolean {
  return getKeywordDefinition(keyword) !== undefined;
}
